import { EventSignUpRegistration } from "../../../types";
import { SpotType } from "../components/RequiredSpot";

const getNumRequiredMemberSlots = (
  minMemberCount: number,
  numMemberRegistrations: number
) => {
  return Math.max(0, minMemberCount - numMemberRegistrations);
};

// Returns the number of required guest slots.
function getNumRequiredGuestSlots(
  minGuestCount: number,
  numGuestRegistrations: number
) {
  return Math.max(0, minGuestCount - numGuestRegistrations);
}

function getNumRequiredCustomerSlots(
  minCustomerCount: number,
  numCustomerRegistrations: number
) {
  return Math.max(0, minCustomerCount - numCustomerRegistrations);
}

// This function returns the number of guest registrations for the given event.
const getNumGuestRegistrations = (
  registrations?: EventSignUpRegistration[]
) => {
  return (
    registrations?.filter(
      (registration) =>
        registration.type === "GUEST" || registration.type === "New Guest"
    ).length ?? 0
  );
};

const getNumMemberRegistrations = (
  registrations?: EventSignUpRegistration[]
) => {
  return (
    registrations?.filter((registration) => registration.type === "MEMBER")
      .length ?? 0
  );
};

const getNumMemberHoldRegistrations = (
  registrations?: EventSignUpRegistration[]
) => {
  return (
    registrations?.filter(
      (registration) =>
        registration.type === "MEMBER" && !registration?.registrant?.playerId
    ).length ?? 0
  );
};

const getNumCustomerHoldRegistrations = (
  registrations?: EventSignUpRegistration[]
) => {
  return (
    registrations?.filter(
      (registration) =>
        registration.type === "CUSTOMER" && !registration?.registrant?.playerId
    ).length ?? 0
  );
};

const getNumCustomerRegistrations = (
  registrations?: EventSignUpRegistration[]
) => {
  return (
    registrations?.filter((registration) => registration.type === "CUSTOMER")
      .length ?? 0
  );
};

export const getRequiredSpots = ({
  minGuestCount,
  minMemberCount,
  allRegistrations,
  supportsPublic,
  minGroupSize,
}: {
  minGuestCount: number;
  minMemberCount: number;
  allRegistrations: EventSignUpRegistration[];
  supportsPublic: boolean;
  minGroupSize: number;
}): SpotType[] => {
  /* we need to cut higher number of members/guests, because of allowed group */

  const numMemberRegistrations = getNumMemberRegistrations(allRegistrations);
  const numGuestRegistrations = getNumGuestRegistrations(allRegistrations);

  const numCustomerRegistrations =
    getNumCustomerRegistrations(allRegistrations);

  const numCustomerHoldRegistrations =
    getNumCustomerHoldRegistrations(allRegistrations);
  const numMemberHoldRegistrations =
    getNumMemberHoldRegistrations(allRegistrations);

  /* We calculate required slots here */
  const numRequiredMemberSlots = supportsPublic
    ? 0 // When supportsPublic is true, members don't have requirements (customers do)
    : getNumRequiredMemberSlots(
        minMemberCount,
        numMemberRegistrations - numMemberHoldRegistrations
      );

  const numRequiredGuestSlots = getNumRequiredGuestSlots(
    minGuestCount,
    numGuestRegistrations
  );

  // When supportsPublic is true, customers are treated like members (using minMemberCount)
  // When supportsPublic is false, members use minMemberCount as usual
  const numRequiredCustomerSlots = supportsPublic
    ? getNumRequiredCustomerSlots(
        minMemberCount,
        numCustomerRegistrations - numCustomerHoldRegistrations
      )
    : 0;
  const numGuestAndMemberRegistrations =
    numMemberRegistrations + numGuestRegistrations;

  const numRequiredFlexSlots = supportsPublic
    ? Math.max(
        minGroupSize - numCustomerRegistrations - numRequiredCustomerSlots,
        0
      )
    : Math.max(
        minGroupSize -
          numGuestAndMemberRegistrations -
          numRequiredMemberSlots -
          numRequiredGuestSlots,
        0
      );

  // Build the ordered array of required slots
  const requiredSlots: SpotType[] = [];

  if (supportsPublic) {
    // For public: CUSTOMER slots, then FLEX slots
    for (let i = 0; i < numRequiredCustomerSlots; i++) {
      requiredSlots.push("CUSTOMER");
    }
  } else {
    // For non-public: MEMBER slots, then GUEST slots
    for (let i = 0; i < numRequiredMemberSlots; i++) {
      requiredSlots.push("MEMBER");
    }
    for (let i = 0; i < numRequiredGuestSlots; i++) {
      requiredSlots.push("GUEST");
    }
  }

  // FLEX slots always come last
  for (let i = 0; i < numRequiredFlexSlots; i++) {
    requiredSlots.push("FLEX");
  }

  return requiredSlots;
};
